// @flow
import R from 'ramda';
import { find, uniqWith, sortBy } from 'lodash';
import _debug from 'debug';
import * as queryString from 'query-string';
import moment from 'moment';
import {
  reduce,
  dateFromPair,
  milesToMeters,
  makeActionCreator
} from '../helpers/utils';
import typeof Client from '../api/Api';
import { isReservable, isJp } from '../helpers/location';
import toggleArray from '../helpers/toggleArray';

const debug = _debug('jp:reducers:search');

type LatLng = {
  lat: number,
  lng: number
};

type ParkingType = 'onstreet' | 'offstreet';

export type Result = {
  coordinates: LatLng,
  id: number,
  photos: Array<string>,
  price: {
    amount: number,
    currency: string,
    formatted: string
  },
  reviews: {
    rating: number,
    count: number
  },
  title: string,
  type: ParkingType,
  slug?: string,
  cluster?: boolean,
  availability: ?number,
  distance: number
};

type SearchFormData = {
  q: string,
  coords: LatLng,
  placeId: string,
  dates: Array<{
    arriving: Date,
    leaving: ?Date,
    monthlyDuration: ?string
  }>,
  filters: string
};

type Filter =
  | {|
      type: 'select',
      value: 'all' | 'driveways' | 'car_parks'
    |}
  | {|
      type: 'with',
      value:
        | 'airport_transfer'
        | 'onsite_staff'
        | 'disabled_access'
        | 'cctv'
        | 'secure_access'
        | 'multiple_entry'
    |}
  | {|
      type: 'sort',
      value: 'best_match' | 'distance' | 'price'
    |};

export const filters = {
  select: {
    all: { type: 'select', value: 'all' },
    driveways: { type: 'select', value: 'driveways' },
    car_parks: { type: 'select', value: 'car_parks' }
  },
  with: {
    airport_transfer: { type: 'with', value: 'airport_transfer' },
    onsite_staff: { type: 'with', value: 'onsite_staff' },
    disabled_access: { type: 'with', value: 'disabled_access' },
    cctv: { type: 'with', value: 'cctv' },
    secure_access: { type: 'with', value: 'secure_access' },
    multiple_entry: { type: 'with', value: 'multiple_entry' }
  },
  sort: {
    best_match: { type: 'sort', value: 'best_match' },
    distance: { type: 'sort', value: 'distance' },
    price: { type: 'sort', value: 'price' }
  }
};

type SelectType = 'all' | 'driveways' | 'car_parks';

/* could do something sexier by making values match the values used in
  the data but that is not as clear as a declarative definition of what
  each filter does */
const getSelectFunc = ({ value }: { value: SelectType }) => {
  switch (value) {
    case 'all': {
      return R.identity;
    }
    case 'driveways': {
      return R.filter((v: { type: ParkingType }) => v.type === 'onstreet');
    }
    case 'car_parks': {
      return R.filter((v: { type: ParkingType }) => v.type === 'offstreet');
    }
    default:
      throw new Error('Unknown select filter type');
  }
};

const getWithFunc = () => R.identity;

const distance = (
  { lat: lat1, lng: lng1 }: LatLng,
  { coordinates: { lat: lat2, lng: lng2 } }: { coordinates: LatLng }
) => {
  const deglen = 110.25;
  const x = lat1 - lat2;
  const y = (lng1 - lng2) * Math.cos(lat2);
  return deglen * Math.sqrt(x * x + y * y);
};

const getSortFunc = (center, { value }) => {
  if (value === 'best_match') {
    return R.identity;
  }
  if (value === 'price') {
    return R.sortBy(v => v.price.amount);
  }
  return R.sortBy(v => v.distance);
};

const getFilterFunc = (center, filter) => {
  switch (filter.type) {
    case 'select': {
      return getSelectFunc(filter);
    }
    case 'with': {
      return getWithFunc();
    }
    case 'sort': {
      return getSortFunc(center, filter);
    }
    default: {
      throw new Error('Unknown filter type');
    }
  }
};

const makeFilters: any = (center, newFilters) =>
  R.reduce(
    (f, fi) =>
      R.compose(
        f,
        getFilterFunc(center, fi)
      ),
    R.identity,
    newFilters
  );

const MONTHLY_UPGRADE_VISIBLE = 'VISIBLE';
const MONTHLY_UPGRADE_DISMISSED = 'DISMISSED';

export type State = {|
  selectedId: string | number,
  hoveredId: string | number,
  datePickerVisible: boolean,
  showLoginWall: boolean,
  showLocationDetails: boolean,
  showFilterPanel: boolean,
  selectedFirstPin: boolean,
  selectedPinDirty: boolean,
  selectedLocationDetail: boolean,
  modalAccordion: boolean,
  showHowItWorks: boolean,
  searchDirty: boolean,
  loadingPins: boolean,
  showReviews: boolean,
  details: { [number]: any },
  pinResults: Array<Result>,
  dismissedTooltip: boolean,
  savedSearch: ?SearchFormData,
  hasSearched: SearchFormData,
  clientSideSearch: boolean,
  wantScroll: boolean,
  showEditSearch: boolean,
  clusters: Array<number>,
  lowestPrice: number,
  filters: Array<Filter>,
  filterResults: Array<Result>,
  filterCheckboxes: Array<string>,
  zoom: number,
  metadata: {
    title: string,
    description: string,
    canonical_url: string,
    bookingType: string
  },
  currentDistanceFromCenter: number,
  currentPanPosition: { lat: number, lng: number },
  dragging: boolean,
  searchTimestamp: number,
  firstLoadComplete: boolean,
  searchInitBy?: string,
  searchInitByValue?: string,
  walkingTimesLoaded: boolean,
  monthlyUpgradeModal: null | 'DISMISSED' | 'VISIBLE',
  hasUpdatedFilterQuery: boolean
|};

const defaultArriving: Date = new Date();
defaultArriving.setHours(8);
defaultArriving.setMinutes(0);

const defaultLeaving: Date = new Date();
defaultLeaving.setHours(18);
defaultLeaving.setMinutes(0);

export const initialState: () => State = () => ({
  selectedId: '_:-1',
  hoveredId: '_:-1',
  searchTimestamp: 0,
  datePickerVisible: false,
  showLoginWall: false,
  showLocationDetails: false,
  showFilterPanel: false,
  selectedFirstPin: false,
  selectedPinDirty: false,
  selectedLocationDetail: false,
  dismissedTooltip: false,
  searchDirty: false,
  savedSearch: null,
  modalAccordion: false,
  showHowItWorks: false,
  loadingPins: true,
  showEditSearch: false,
  showReviews: false,
  details: {},
  pinResults: [],
  hasSearched: {
    q: '',
    placeId: '',
    dates: [
      {
        arriving: defaultArriving,
        leaving: defaultLeaving,
        monthlyDuration: null
      }
    ],
    coords: {
      lat: 51.48918,
      lng: -0.212642
    },
    filters: ''
  },
  clientSideSearch: false,
  wantScroll: false,
  clusters: [],
  filters: [
    {
      type: 'sort',
      value: 'best_match'
    }
  ],
  filterResults: [],
  filterCheckboxes: [],
  lowestPrice: 100,
  zoom: 15,
  metadata: {
    title: '',
    description: '',
    canonical_url: '',
    bookingType: ''
  },
  currentDistanceFromCenter: 0,
  currentPanPosition: {
    lat: 51.48918,
    lng: -0.212642
  },
  dragging: false,
  firstLoadComplete: false,
  walkingTimesLoaded: false,
  monthlyUpgradeModal: null,
  hasUpdatedFilterQuery: false
});

export const SELECT_ITEM = 'search/SELECT_ITEM';
const HOVER_ITEM_ENTER = 'search/HOVER_ITEM_ENTER';
const HOVER_ITEM_EXIT = 'search/HOVER_ITEM_EXIT';
const TOGGLE_DATEPICKER = 'search/TOGGLE_DATEPICKER';
const TOGGLE_MODAL_ACCORDION = 'search/TOGGLE_MODAL_ACCORDION';
const TOGGLE_HOW_IT_WORKS = 'search/TOGGLE_HOW_IT_WORKS';
const TOGGLE_LOCATION_DETAILS = 'search/TOGGLE_LOCATION_DETAILS';
const TOGGLE_LOGIN_WALL = 'search/TOGGLE_LOGIN_WALL';
export const TOGGLE_FILTER_PANEL = 'search/TOGGLE_FILTER_PANEL';
const LOAD_PINS = 'search/LOAD_PINS';
const LOAD_PINS_SUCCESS = 'search/LOAD_PINS_SUCCESS';
const LOAD_PINS_FAIL = 'search/LOAD_PINS_FAIL';
export const SEARCH_AREA = 'search/SEARCH_AREA';
const SET_AREA = 'search/SET_AREA';
export const MAP_SELECT_ITEM = 'search/MAP_SELECT_ITEM';
const LOAD_REVIEW = 'search/LOAD_REVIEW';
const LOAD_REVIEW_SUCCESS = 'search/LOAD_REVIEW_SUCCESS';
const LOAD_REVIEW_FAIL = 'search/LOAD_REVIEW_FAIL';
const CLOSE_REVIEW = 'search/CLOSE_REVIEW';
const UPDATE_CLUSTERS = 'search/UPDATE_CLUSTERS';
const LOAD_DETAILS_FAIL = 'search/LOAD_DETAILS_FAIL';
const LOAD_DETAILS_SUCCESS = 'search/LOAD_DETAILS_SUCCESS';
const CLOSE_DETAILS = 'search/CLOSE_DETAILS';
export const TOGGLE_TAB_FILTER = 'search/TOGGLE_TAB_FILTER';
const SET_ZOOM = 'search/SET_ZOOM';
const DISMISSED_TOOLTIP = 'search/DISMISSED_TOOLTIP';
const CLOSE_ALL_PANELS = 'search/CLOSE_ALL_PANELS';
const PANNED = 'search/PANNED';
const REDO_SEARCH = 'search/REDO_SEARCH';
const SET_CENTER = 'search/SET_CENTER';
const START_DRAG = 'search/START_DRAG';
const SET_DATE = 'search/SET_DATE';
const SET_DIRTY = 'search/SET_DIRTY';
const LOCATION_CHANGE = '@@router/LOCATION_CHANGE';
const SET_SEARCH_INITIALISER = 'search/SET_SEARCH_INITIALISER';
const LOAD_WALKING_DISTANCE = 'search/LOAD_WALKING_DISTANCE';
const LOAD_WALKING_DISTANCE_SUCCESS = 'search/LOAD_WALKING_DISTANCE_SUCCESS';
const LOAD_WALKING_DISTANCE_FAIL = 'search/LOAD_WALKING_DISTANCE_FAIL';
const SET_MONTHLY_UPGRADE_MESSAGE = 'search/SET_MONTHLY_UPGRADE_MESSAGE';
const DISMISS_MONTHLY_UPGRADE_MESSAGE =
  'search/DISMISS_MONTHLY_UPGRADE_MESSAGE';
export const TOGGLE_FILTER_CHECKBOX = 'search/TOGGLE_FILTER_CHECKBOX ';
export const CLEAR_FILTER_CHECKBOXES = 'search/CLEAR_FILTER_CHECKBOXES ';
export const SET_FILTER_CHECKBOXES = 'search/SET_FILTER_CHECKBOXES ';
export const HAS_UPDATED_FILTER_QUERY = 'search/HAS_UPDATED_FILTER_QUERY';

export type Action =
  | {| type: typeof SELECT_ITEM, id: number |}
  | {| type: typeof HOVER_ITEM_ENTER, id: number |}
  | {| type: typeof HAS_UPDATED_FILTER_QUERY |}
  | {| type: typeof DISMISS_MONTHLY_UPGRADE_MESSAGE |}
  | {| type: typeof HOVER_ITEM_EXIT |}
  | {| type: typeof TOGGLE_DATEPICKER |}
  | {| type: typeof TOGGLE_MODAL_ACCORDION |}
  | {| type: typeof TOGGLE_HOW_IT_WORKS |}
  | {| type: typeof TOGGLE_LOCATION_DETAILS |}
  | {| type: typeof TOGGLE_LOGIN_WALL |}
  | {| type: typeof TOGGLE_FILTER_CHECKBOX |}
  | {| type: typeof CLEAR_FILTER_CHECKBOXES |}
  | {| type: typeof SET_FILTER_CHECKBOXES |}
  | {| type: typeof TOGGLE_FILTER_PANEL |}
  | {| type: typeof LOAD_PINS |}
  | {| type: typeof SET_AREA, area: any |}
  | {| type: typeof LOAD_PINS_SUCCESS, payload: any |}
  | {| type: typeof LOAD_PINS_FAIL |}
  | {| type: typeof SEARCH_AREA, area: SearchFormData |}
  | {| type: typeof MAP_SELECT_ITEM, id: number |}
  | {| type: typeof LOAD_REVIEW |}
  | {| type: typeof LOAD_REVIEW_SUCCESS |}
  | {| type: typeof LOAD_REVIEW_FAIL |}
  | {| type: typeof CLOSE_REVIEW |}
  | {| type: typeof UPDATE_CLUSTERS, ids: Array<number>, selected: string |}
  | {| type: typeof LOAD_DETAILS_FAIL |}
  | {| type: typeof LOAD_DETAILS_SUCCESS |}
  | {| type: typeof CLOSE_DETAILS |}
  | {| type: typeof TOGGLE_TAB_FILTER, filter: Filter |}
  | {| type: typeof SET_ZOOM, zoom: number |}
  | {| type: typeof DISMISSED_TOOLTIP |}
  | {| type: typeof PANNED, payload: { lat: number, lng: number } |}
  | {| type: typeof REDO_SEARCH |}
  | {| type: typeof CLOSE_ALL_PANELS |}
  | {| type: typeof START_DRAG |}
  | {| type: typeof SET_DIRTY |}
  | {|
      type: typeof SET_DATE,
      payload: Array<{
        arriving: Date,
        leaving: ?Date,
        monthlyDuration: ?string
      }>
    |}
  | {| type: typeof SET_CENTER, payload: { lat: number, lng: number } |}
  | {| type: typeof LOAD_WALKING_DISTANCE, payload: any |}
  | {| type: typeof LOAD_WALKING_DISTANCE_SUCCESS, payload: any |}
  | {| type: typeof LOAD_WALKING_DISTANCE_FAIL, payload: any |}
  | {|
      type: typeof SET_SEARCH_INITIALISER,
      payload: { name: string, value: string }
    |}
  | {|
      type: typeof LOCATION_CHANGE,
      payload: { pathname: string, search: string, hash: string, key: string }
    |};

export const parseArea = (
  state: any,
  action: { type: typeof SEARCH_AREA, area: any }
) => {
  const area = JSON.parse(JSON.stringify(action.area));

  if (!area.coords) {
    return state;
  }

  const [lat, lng] = area.coords.split(',').map(v => parseFloat(v));

  let arriving;
  let leaving;
  let monthlyDuration;

  if (
    !area.arriving &&
    area.start_date &&
    area.start_time &&
    area.end_date &&
    area.end_time
  ) {
    arriving = dateFromPair(area.start_date, area.start_time);
    leaving = dateFromPair(area.end_date, area.end_time);
    monthlyDuration = null;
  } else {
    const months = parseInt(area.monthly_duration, 10);
    const isMonthly = Number.isNaN(months) === false;

    if (area.arriving) {
      arriving = new Date(area.arriving);
    } else if (area.start_date && area.start_time) {
      arriving = dateFromPair(area.start_date, area.start_time);
    } else {
      arriving = defaultArriving;
    }

    if (isMonthly) {
      leaving = null;
      monthlyDuration = String(months);
    } else {
      leaving = area.leaving ? new Date(area.leaving) : defaultLeaving;
      monthlyDuration = null;
    }
  }

  return {
    ...state,
    hasSearched: {
      q: area.q,
      placeId: area.place_id,
      coords: {
        lat,
        lng
      },
      dates: [
        {
          leaving,
          arriving,
          monthlyDuration
        }
      ],
      filters: area.filters
    }
  };
};

const getMonthlyUpgradeModalState = (state: State) => {
  if (state.monthlyUpgradeModal === MONTHLY_UPGRADE_DISMISSED) {
    return state.monthlyUpgradeModal;
  }

  const { arriving, leaving } = state.hasSearched.dates[0];

  if (leaving === null) {
    return state.monthlyUpgradeModal;
  }

  const dayDiff = moment(leaving).diff(arriving, 'days');

  if (dayDiff >= 25) {
    return MONTHLY_UPGRADE_VISIBLE;
  }

  return state.monthlyUpgradeModal;
};

export default reduce<State, Action>(initialState(), {
  [START_DRAG](state, action: Action) {
    debug(START_DRAG, action);
    return {
      ...state,
      dragging: true
    };
  },
  [SET_CENTER](state, action) {
    const { lat, lng } = action.payload;
    return {
      ...state,
      currentDistanceFromCenter: 0,
      currentPanPosition: {
        lat,
        lng
      },
      hasSearched: {
        ...state.hasSearched,
        coords: { lat, lng }
      }
    };
  },
  [PANNED](state, action) {
    debug(PANNED, action);
    const { lat, lng } = state.hasSearched.coords;
    const { payload } = action;
    return {
      ...state,
      dragging: false,
      currentPanPosition: {
        lat: payload.lat,
        lng: payload.lng
      },
      currentDistanceFromCenter: distance(
        { lat, lng },
        {
          coordinates: {
            lat: payload.lat,
            lng: payload.lng
          }
        }
      )
    };
  },
  [SELECT_ITEM](state, action) {
    debug(SELECT_ITEM);
    const { id } = action;
    return {
      ...state,
      selectedId: id,
      wantScroll: false,
      showLocationDetails: true,
      showFilterPanel: false,
      selectedLocationDetail: true
    };
  },
  [HOVER_ITEM_ENTER](state, action) {
    debug(HOVER_ITEM_ENTER);
    const { id } = action;
    return {
      ...state,
      hoveredId: id
    };
  },
  [HOVER_ITEM_EXIT](state) {
    debug(HOVER_ITEM_EXIT);
    return {
      ...state,
      hoveredId: -1
    };
  },
  [MAP_SELECT_ITEM](state, action) {
    debug(MAP_SELECT_ITEM);
    const { id, mobile } = action;
    return {
      ...state,
      selectedId: id,
      wantScroll: true,
      selectedFirstPin: !state.selectedPinDirty && !state.selectedFirstPin,
      selectedPinDirty: true,
      showLocationDetails: !mobile,
      showFilterPanel: false,
      showReviews: id === state.selectedId ? state.showReviews : false
    };
  },
  [TOGGLE_DATEPICKER](state) {
    debug(TOGGLE_DATEPICKER);
    const { datePickerVisible } = state;
    return {
      ...state,
      datePickerVisible: !datePickerVisible
    };
  },
  [TOGGLE_MODAL_ACCORDION](state) {
    debug(TOGGLE_MODAL_ACCORDION);
    const { modalAccordion } = state;
    return {
      ...state,
      modalAccordion: !modalAccordion
    };
  },
  [TOGGLE_HOW_IT_WORKS](state) {
    debug(TOGGLE_HOW_IT_WORKS);
    const { showHowItWorks } = state;
    return {
      ...state,
      showHowItWorks: !showHowItWorks
    };
  },
  [TOGGLE_LOCATION_DETAILS](state) {
    debug(TOGGLE_LOCATION_DETAILS);
    const { showLocationDetails } = state;
    return {
      ...state,
      showLocationDetails: !showLocationDetails
    };
  },
  [TOGGLE_LOGIN_WALL](state) {
    debug(TOGGLE_LOGIN_WALL);
    const { showLoginWall } = state;
    return {
      ...state,
      showLoginWall: !showLoginWall
    };
  },
  [CLOSE_DETAILS](state) {
    debug(CLOSE_DETAILS);
    return {
      ...state,
      selectedId: -1,
      showLocationDetails: false
    };
  },
  [LOAD_REVIEW](state, action) {
    debug(LOAD_REVIEW);
    return {
      ...state,
      showReviews: action.show === undefined ? true : action.show
    };
  },
  [LOAD_REVIEW_SUCCESS](state, action) {
    debug(LOAD_REVIEW_SUCCESS);
    const {
      id,
      payload: { data }
    } = action;

    const results = state.pinResults.map(result => {
      if (result.id === id) {
        return {
          ...result,
          reviews: {
            ...result.reviews,
            data: data === null ? [] : data
          }
        };
      }
      return result;
    });
    const filterFunc = makeFilters(state.hasSearched.coords, state.filters);
    return {
      ...state,
      pinResults: results,
      details: {
        ...state.details,
        [id]: {
          ...state.details[id],
          reviews: data
        }
      },
      filterResults: filterFunc(results)
    };
  },
  [LOAD_PINS](state) {
    debug(LOAD_PINS);
    return {
      ...state,
      loadingPins: true,
      showReviews: false,
      showLocationDetails: false
    };
  },
  [LOAD_PINS_SUCCESS](state, action) {
    debug(LOAD_PINS_SUCCESS);
    const {
      payload: { data, meta }
    } = action;

    const upins = uniqWith(
      data,
      (arrVal, other) =>
        arrVal.coordinates.lat === other.coordinates.lat &&
        arrVal.coordinates.lng === other.coordinates.lng
    );

    const pinsSortedByDistance = sortBy(upins, ['distance']);
    const closestJustParkPin = find(pinsSortedByDistance, pin =>
      isReservable(pin)
    );
    const closestJustParkPinDistance = closestJustParkPin
      ? milesToMeters(closestJustParkPin.distance)
      : 0;

    let dataPins = 0;
    const fpins = upins.filter(pin => {
      // Hide any data pins when the first JustPark pin is within 500 meters
      if (
        closestJustParkPin &&
        closestJustParkPinDistance <= 500 &&
        !isJp(pin)
      ) {
        return false;
      }

      // Show a maximum of 4 data pins
      if (!isJp(pin)) {
        // We don't show data pins that are further away than the closest JustPark pin
        if (
          closestJustParkPin &&
          (dataPins > 3 || pin.distance > closestJustParkPin.distance)
        ) {
          return false;
        }
        dataPins += 1;
      }

      return true;
    });

    const pinResults = fpins || state.pinResults;
    const res = fpins || state.filterResults;
    const filterResults = res.map(result => ({
      ...result,
      distanceInMeters: milesToMeters(result.distance)
    }));

    return {
      ...state,
      loadingPins: false,
      metadata: meta,
      clientSideSearch: true,
      selectedId: '_:-1',
      pinResults: pinResults.map(result => ({
        ...result,
        price: {
          ...result.price,
          symbol:
            result.price.formatted !== 'Free'
              ? result.price.formatted.slice(0, 1)
              : null
        }
      })),
      lowestPrice: pinResults.reduce(
        ([low, str], val) =>
          low > val.price.amount
            ? [val.price.amount, val.price.formatted]
            : [low, str],
        [Infinity, '']
      ),
      filterResults,
      firstLoadComplete: true,
      // We don't refilter the incoming results so reset the filter value
      filters: [
        {
          type: 'sort',
          value: 'best_match'
        }
      ]
    };
  },
  [SET_AREA](state, action) {
    debug(SET_AREA, action);
    const parsed = parseArea(state, action);
    return {
      ...parsed,
      pinResults: [],
      currentPanPosition: {
        lat: parsed.hasSearched.coords.lat,
        lng: parsed.hasSearched.coords.lng
      }
    };
  },
  [SET_DATE](state, action) {
    debug(SET_DATE, action);

    const { arriving, monthlyDuration } = action.payload[0];

    const datesPayload =
      monthlyDuration === '-1'
        ? [
            {
              arriving,
              monthlyDuration: null,
              leaving: moment(arriving).add(1, 'day')
            }
          ]
        : action.payload;

    return {
      ...state,
      savedSearch: state.savedSearch || state.hasSearched,
      hasSearched: {
        ...state.hasSearched,
        dates: datesPayload
      }
    };
  },
  [SEARCH_AREA](state, action) {
    debug(SEARCH_AREA);
    const newState = parseArea(state, action);

    return {
      ...newState,
      savedSearch: null,
      clientSideSearch: true,
      showEditSearch: false,
      searchTimestamp: +new Date()
    };
  },
  [SET_MONTHLY_UPGRADE_MESSAGE](state) {
    debug(SET_MONTHLY_UPGRADE_MESSAGE);
    const monthlyUpgradeModal = getMonthlyUpgradeModalState(state);

    return {
      ...state,
      monthlyUpgradeModal
    };
  },
  [DISMISS_MONTHLY_UPGRADE_MESSAGE](state) {
    return {
      ...state,
      monthlyUpgradeModal: MONTHLY_UPGRADE_DISMISSED
    };
  },
  [UPDATE_CLUSTERS](state, action) {
    debug(UPDATE_CLUSTERS);
    const { ids, selected } = action;
    if (ids.length === state.clusters.length && selected === state.selectedId) {
      return state;
    }
    return {
      ...state,
      clusters: ids
    };
  },
  [LOAD_DETAILS_SUCCESS](state, action) {
    debug(LOAD_DETAILS_SUCCESS);
    const {
      id,
      payload: { data }
    } = action;

    const result = find(state.pinResults, r => r.id === id);

    if (!result) {
      return {
        ...state,
        selectedId: null,
        showLocationDetails: false,
        selectedLocationDetail: false
      };
    }

    return {
      ...state,
      details: {
        ...state.details,
        [id]: {
          ...data,
          id,
          type:
            result.type === 'enquiry' ||
            result.type === 'driveup' ||
            result.type === 'driveup-onstreet'
              ? result.type
              : data.type,
          availability: result.availability,
          price: result.price
        },
        searchTimestamp: state.searchTimestamp
      }
    };
  },
  [LOAD_DETAILS_FAIL](state) {
    debug(LOAD_DETAILS_FAIL);
    return {
      ...state,
      selectedId: null,
      showLocationDetails: false,
      selectedLocationDetail: false
    };
  },
  [TOGGLE_TAB_FILTER](state, action) {
    debug(TOGGLE_TAB_FILTER);
    const { filter } = action;
    if (
      state.filters.indexOf(filter) > -1 &&
      (filter.type !== 'select' && filter.type !== 'sort')
    ) {
      const newFilters = state.filters.filter(f => f !== filter);
      const filterFunc = makeFilters(state.hasSearched.coords, newFilters);
      return {
        ...state,
        showReviews: false,
        showLocationDetails: false,
        filters: newFilters,
        filterResults: filterFunc(state.pinResults)
      };
    }
    let newFilters = state.filters.slice();
    newFilters.unshift(filter);
    newFilters = R.uniqBy(
      f => (f.type === 'with' ? f.value : f.type),
      newFilters
    );
    const filterFunc = makeFilters(state.hasSearched.coords, newFilters);

    return {
      ...state,
      showReviews: false,
      showLocationDetails: false,
      filters: newFilters,
      filterResults: filterFunc(state.pinResults)
    };
  },
  [SET_ZOOM](state, action) {
    debug(SET_ZOOM);
    const { zoom } = action;
    return {
      ...state,
      zoom
    };
  },
  [CLOSE_ALL_PANELS](state) {
    debug(CLOSE_ALL_PANELS);

    return {
      ...state,
      showLocationDetails: false,
      showFilterPanel: false,
      showReviews: false
    };
  },
  [DISMISSED_TOOLTIP](state) {
    debug(DISMISSED_TOOLTIP);
    return {
      ...state,
      dismissedTooltip: true
    };
  },
  [LOCATION_CHANGE](state, action) {
    debug(LOCATION_CHANGE, action);
    const {
      payload: {
        location: { search }
      }
    } = action;

    const queryParams = { ...queryString.parse(search) };

    if (state.showReviews && !queryParams.reviews) {
      return {
        ...state,
        showReviews: false
      };
    }

    if (state.selectedId && state.showLocationDetails) {
      if (!queryParams.listingId && !queryParams.listingType) {
        return {
          ...state,
          selectedId: null,
          showLocationDetails: false,
          showReviews: false
        };
      }
    }

    return {
      ...state
    };
  },
  [SET_SEARCH_INITIALISER](state, action) {
    debug(SET_SEARCH_INITIALISER, action);
    return {
      ...state,
      searchInitBy: action.payload.name,
      searchInitByValue: action.payload.value,
      hasSearched: {
        ...state.hasSearched,
        dates: [action.payload.dates]
      }
    };
  },
  [LOAD_WALKING_DISTANCE](state) {
    return {
      ...state,
      walkingTimesLoaded: false
    };
  },
  [LOAD_WALKING_DISTANCE_SUCCESS](state, action) {
    debug(LOAD_WALKING_DISTANCE_SUCCESS, action);

    const walkingTimes = {};

    action.payload.data.forEach(item => {
      walkingTimes[item.listingId] = item.time;
    });

    // Push the walking times into the pin results
    const pinResults = state.pinResults.map(item => ({
      ...item,
      walkingTime: walkingTimes[item.id] || null
    }));

    // Perform filter again so that filterResults contains the latest data
    const filterFunc = makeFilters(state.hasSearched.coords, state.filters);

    return {
      ...state,
      pinResults,
      filterResults: filterFunc(pinResults),
      walkingTimesLoaded: true
    };
  },
  [TOGGLE_FILTER_PANEL](state) {
    debug(TOGGLE_FILTER_PANEL);
    const {
      hasUpdatedFilterQuery,
      showFilterPanel,
      showLocationDetails
    } = state;

    return {
      ...state,
      showFilterPanel: !showFilterPanel,
      showLocationDetails: !showFilterPanel ? false : showLocationDetails,
      hasUpdatedFilterQuery:
        hasUpdatedFilterQuery && !showFilterPanel
          ? false
          : hasUpdatedFilterQuery
    };
  },
  [TOGGLE_FILTER_CHECKBOX](state, action) {
    debug(TOGGLE_FILTER_CHECKBOX, action);
    const value = action.payload;
    const filterCheckboxes = [...state.filterCheckboxes];
    const newFilters = toggleArray(filterCheckboxes, value);

    return {
      ...state,
      filterCheckboxes: newFilters,
      hasSearched: {
        ...state.hasSearched,
        filters: newFilters.join(',')
      }
    };
  },
  [SET_FILTER_CHECKBOXES](state, action) {
    debug(SET_FILTER_CHECKBOXES, action);

    const filterCheckboxes = [...action.payload, ...state.filterCheckboxes];

    return {
      ...state,
      filterCheckboxes
    };
  },
  [CLEAR_FILTER_CHECKBOXES](state) {
    debug(CLEAR_FILTER_CHECKBOXES);

    return {
      ...state,
      filterCheckboxes: []
    };
  },
  [HAS_UPDATED_FILTER_QUERY](state) {
    debug(HAS_UPDATED_FILTER_QUERY);

    return {
      ...state,
      hasUpdatedFilterQuery: true
    };
  }
});

export const setSearchInitialiser = (name: string, value: string, dates) => ({
  type: SET_SEARCH_INITIALISER,
  payload: {
    name,
    value,
    dates
  }
});

export const toggleDatepicker = (): Action => ({ type: TOGGLE_DATEPICKER });

export const toggleModalAccordion = (): Action => ({
  type: TOGGLE_MODAL_ACCORDION
});

export const toggleHowItWorks = (): Action => ({ type: TOGGLE_HOW_IT_WORKS });

export const toggleLocationDetails = (): Action => ({
  type: TOGGLE_LOCATION_DETAILS
});

export const toggleLoginWall = (): Action => ({ type: TOGGLE_LOGIN_WALL });

export const setMonthlyUpgradeMessage = (): Action => ({
  type: SET_MONTHLY_UPGRADE_MESSAGE
});

export const searchArea = (area: SearchFormData) => (
  dispatch: any,
  getState: any
) => {
  const {
    browser: {
      greaterThan: { small: gtSmall }
    }
  } = getState();

  dispatch({
    type: SEARCH_AREA,
    area
  });

  if (gtSmall) {
    dispatch(setMonthlyUpgradeMessage());
  }
};

export function setArea(area: any) {
  return (dispatch: (*) => *) => {
    dispatch({ type: SET_AREA, area });
    return new Promise<*>(resolve => setTimeout(resolve, 0));
  };
}

export const showReview = (): Action => ({ type: LOAD_REVIEW });

export const updateClusters = (
  ids: Array<number>,
  selected: string
): Action => ({ type: UPDATE_CLUSTERS, ids, selected });

export const closeDetails = (): Action => ({ type: CLOSE_DETAILS });

export const toggleTabFilter = (filter: Filter): Action => ({
  type: TOGGLE_TAB_FILTER,
  filter
});

export const setPanned = (payload: { lat: number, lng: number }) => ({
  type: PANNED,
  payload
});

export const onStartDrag = () => ({ type: START_DRAG });

export function redoSearch() {
  return (dispatch: any, getState: any) => {
    dispatch({
      type: SET_CENTER,
      payload: getState().search.currentPanPosition
    });

    const params = getState().search.hasSearched;
    const { arriving, leaving, monthlyDuration } = params.dates[0];

    const dateFormat = 'YYYY-MM-DD HH:mm';
    const startDate = moment(arriving).format(dateFormat);
    const endDate = leaving ? moment(leaving).format(dateFormat) : null;

    const endOrDuration = endDate
      ? { end_date: endDate }
      : { duration: monthlyDuration };

    const searchParams = {
      lat: params.coords.lat,
      lng: params.coords.lng,
      start_date: startDate,
      ...endOrDuration,
      google_place_id: params.placeId,
      user_query: params.q,
      filters: params.filters
    };

    dispatch({
      types: [LOAD_PINS, LOAD_PINS_SUCCESS, LOAD_PINS_FAIL],
      promise: client => {
        return client.geo.search(searchParams);
      }
    });
  };
}

export const toggleTooltip = (): Action => ({ type: DISMISSED_TOOLTIP });

export const dismissMonthlyUpgradeMessage = (): Action => ({
  type: DISMISS_MONTHLY_UPGRADE_MESSAGE
});

export const setZoom = (zoom: number): Action => ({ type: SET_ZOOM, zoom });

export const setDate = (dates: any): Action => ({
  type: SET_DATE,
  payload: dates
});

export const closeAllPanels = (): Action => ({ type: CLOSE_ALL_PANELS });

export function loadReviews(type: string, id: number, show?: boolean = true) {
  return (dispatch: (*) => Promise<*>, getState: () => { search: State }) => {
    const item = getState().search.pinResults.filter(r => r.id === id)[0];
    if (item.reviews.count > 0) {
      dispatch({
        id,
        show,
        types: [LOAD_REVIEW, LOAD_REVIEW_SUCCESS, LOAD_REVIEW_FAIL],
        promise: (client: Client) => client.locationDetails.reviews(item.id)
      });
    }
    if (item.reviews.count === 0) {
      dispatch({
        id,
        type: LOAD_REVIEW_SUCCESS,
        payload: {
          data: null
        }
      });
    }
  };
}

export const loadWalkingDistance = () => (
  dispatch: (*) => Promise<*>,
  getState: () => { search: State }
) => {
  const {
    search: {
      pinResults,
      hasSearched: { coords }
    }
  } = getState();

  const listingIds = pinResults.map(p => p.id);

  if (listingIds.length === 0) {
    return;
  }

  dispatch({
    types: [
      LOAD_WALKING_DISTANCE,
      LOAD_WALKING_DISTANCE_SUCCESS,
      LOAD_WALKING_DISTANCE_FAIL
    ],
    promise: (client: Client) => client.routing.walking(coords, listingIds)
  });
};

export function selectItem(type: string, id: number) {
  return (dispatch: (*) => Promise<*>, getState: () => { search: State }) => {
    const state = getState();
    const item = state.search.details[id];

    if (!item || item.searchTimestamp !== state.search.searchTimestamp) {
      dispatch({
        id,
        types: [SELECT_ITEM, LOAD_DETAILS_SUCCESS, LOAD_DETAILS_FAIL],
        promise: (client: Client) =>
          client.locationDetails.info(type, id).then(details => {
            if (
              details.data.feedback &&
              details.data.feedback.count > 0 &&
              details.data.feedback.latestReview &&
              parseFloat(details.data.feedback.latestReview.rating) < 4
            ) {
              dispatch(loadReviews(details.data.type, details.data.id, false));
            }
            return details;
          })
      });
    } else {
      dispatch({ type: SELECT_ITEM, id });
    }
  };
}

export function onMouseOver(type: string, id: number) {
  return (dispatch: (*) => Promise<*>) => {
    dispatch({ type: HOVER_ITEM_ENTER, id });
  };
}
export function onMouseOut() {
  return (dispatch: (*) => Promise<*>) => {
    dispatch({ type: HOVER_ITEM_EXIT });
  };
}

export function loadPins(
  enquiries: number = 0,
  loadListing = false,
  listingType,
  listingId
) {
  return (dispatch: (*) => Promise<*>, getState: () => { search: State }) =>
    dispatch({
      types: [LOAD_PINS, LOAD_PINS_SUCCESS, LOAD_PINS_FAIL],
      promise: (client: Client) => {
        const params = (getState ? getState().search : initialState())
          .hasSearched;

        const { arriving, leaving, monthlyDuration } = params.dates[0];

        const dateFormat = 'YYYY-MM-DD HH:mm';
        const startDate = moment(arriving).format(dateFormat);
        const endDate = leaving ? moment(leaving).format(dateFormat) : null;

        const endOrDuration = endDate
          ? { end_date: endDate }
          : { duration: String(monthlyDuration) };

        const data = {
          lat: params.coords.lat,
          lng: params.coords.lng,
          start_date: startDate,
          google_place_id: params.placeId,
          user_query: params.q,
          filters: params.filters,
          enquiries,
          ...endOrDuration
        };

        return client.geo.search(data).then(response => {
          // Handles the case where we have a listingId thats stored in a
          // queryParam to trigger the load of details for that result
          if (loadListing) {
            setImmediate(() => {
              dispatch(selectItem(listingType, listingId));
            });
          }
          return response;
        });
      }
    });
}

export function mapSelectItem(type: string, id: number) {
  return (
    dispatch: (*) => Promise<*>,
    getState: () => { search: State, browser: any }
  ) => {
    const state = getState();
    const item = state.search.details[id];
    const mobile = state.browser.is.extraSmall;
    if (!item || item.searchTimestamp !== state.search.searchTimestamp) {
      dispatch({
        id,
        mobile,
        types: [MAP_SELECT_ITEM, LOAD_DETAILS_SUCCESS, LOAD_DETAILS_FAIL],
        promise: (client: Client) => client.locationDetails.info(type, id)
      });
    } else {
      dispatch({ type: MAP_SELECT_ITEM, id, mobile });
    }
  };
}

export const toggleFilterPanel = makeActionCreator(TOGGLE_FILTER_PANEL);
export const toggleFilterCheckbox = makeActionCreator(
  TOGGLE_FILTER_CHECKBOX,
  'payload'
);
export const setFilterCheckboxes = makeActionCreator(
  SET_FILTER_CHECKBOXES,
  'payload'
);
export const clearFilterCheckbox = makeActionCreator(CLEAR_FILTER_CHECKBOXES);
export const hasUpdatedFilterQuery = makeActionCreator(
  HAS_UPDATED_FILTER_QUERY
);

export const isMonthlyUpgradeModalVisible = (state: { search: State }) =>
  state.search.monthlyUpgradeModal === MONTHLY_UPGRADE_VISIBLE;

type Analytics = {|
  track: (string, ?{}, ?{}) => void,
  page: (string, ?{}, ?{}) => void
|};

export const analyticsEventSubscribers = {
  [TOGGLE_TAB_FILTER]: (action: Action, analytics: Analytics) => {
    if (action.type === TOGGLE_TAB_FILTER) {
      if (action.filter.type === 'sort') {
        analytics.track('action:search-results:sorting-changed', action.filter);
      }
      if (action.filter.type === 'select') {
        analytics.track('action:search-results:filter-set', action.filter);
      }
    }
  }
};
